import { DesignTheme } from '../types'
import { ALERT_COLORS } from './alertPalette'

/**
 * Default light theme — minimal shell:
 * white canvas, soft accent dots, high-contrast FAB, large rounded cards.
 * Album cards use PHOTO covers; accent colors are accents only.
 */
export const defaultTheme: DesignTheme = {
  id: 'default',
  name: 'Default',
  mode: 'light',
  color: {
    canvas: '#FFFFFF',
    surface: '#FFFFFF',
    ink: '#111111',
    inkMuted: '#6B6B6B',
    inkSubtle: '#A3A3A3',
    accent: '#111111',
    accentInk: '#FFFFFF',
    border: 'rgba(17, 17, 17, 0.1)',
    borderStrong: 'rgba(17, 17, 17, 0.18)',
    hover: 'rgba(17, 17, 17, 0.04)',
    inverse: '#111111',
    inverseInk: '#FFFFFF',
    danger: '#E11D48',
    success: '#10B981',
    warning: '#F59E0B',
    alert: ALERT_COLORS,
    photoOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)',
    immersive: '#0A0A0A',
    immersiveInk: '#FFFFFF',
    night: {
      canvas: '#0A0A0A',
      surface: '#161616',
      ink: '#F5F5F5',
      inkMuted: 'rgba(245, 245, 245, 0.6)',
      inkSubtle: 'rgba(245, 245, 245, 0.36)',
      border: 'rgba(245, 245, 245, 0.1)',
    },
  },
  accentDots: ['#ffcf7d', '#fea67d', '#be9dfd', '#01dafe', '#e7f09a'],
  radius: {
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    pill: 999,
  },
  space: {
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 24,
    6: 32,
    7: 48,
    8: 64,
  },
  type: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    weightRegular: 400,
    weightMedium: 500,
    weightSemibold: 600,
    weightBold: 700,
    sizeXs: 12,
    sizeSm: 14,
    sizeMd: 16,
    sizeLg: 20,
    sizeXl: 28,
    sizeDisplay: 40,
    lineTight: 1.15,
    lineNormal: 1.45,
  },
  shadow: {
    none: 'none',
    soft: '0 8px 24px rgba(17, 17, 17, 0.06)',
    lift: '0 16px 40px rgba(17, 17, 17, 0.12)',
  },
  motion: {
    fast: '150ms',
    normal: '250ms',
    slow: '400ms',
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
}

/** Dark companion — same structure, ready for toggle */
export const defaultDarkTheme: DesignTheme = {
  ...defaultTheme,
  id: 'default-dark',
  name: 'Default Dark',
  mode: 'dark',
  color: {
    ...defaultTheme.color,
    canvas: '#0A0A0A',
    surface: '#161616',
    ink: '#F5F5F5',
    inkMuted: '#A3A3A3',
    inkSubtle: '#737373',
    accent: '#F5F5F5',
    accentInk: '#0A0A0A',
    border: 'rgba(255, 255, 255, 0.1)',
    hover: 'rgba(255, 255, 255, 0.06)',
    inverse: '#F5F5F5',
    inverseInk: '#0A0A0A',
    photoOverlay: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%)',
    immersive: '#000000',
    immersiveInk: '#FFFFFF',
  },
  shadow: {
    none: 'none',
    soft: '0 8px 24px rgba(0, 0, 0, 0.35)',
    lift: '0 16px 40px rgba(0, 0, 0, 0.5)',
  },
}
